import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";

// Layouts
import DashboardLayout from "@/layouts/DashboardLayout";
import AdminLayout from "@/layouts/AdminLayout";

// Public Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/auth/LoginPage";
import RegisterPage from "@/pages/auth/RegisterPage";
import VerifyOTPPage from "@/pages/auth/VerifyOTPPage";
import AdminLoginPage from "@/pages/auth/AdminLoginPage";

// User Pages
import UserDashboard from "@/pages/user/Dashboard";
import DepositsPage from "@/pages/user/Deposits";
import WithdrawalsPage from "@/pages/user/Withdrawals";
import InvestmentsPage from "@/pages/user/Investments";
import NotificationsPage from "@/pages/user/Notifications";
import ProfilePage from "@/pages/user/Profile";
import ReferralsPage from "@/pages/user/Referrals";

// Admin Pages
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminUsersPage from "@/pages/admin/Users";
import AdminUserDetailPage from "@/pages/admin/UserDetail";
import AdminDepositsPage from "@/pages/admin/Deposits";
import AdminWithdrawalsPage from "@/pages/admin/Withdrawals";
import AdminProfitsPage from "@/pages/admin/Profits";
import AdminAuditLogPage from "@/pages/admin/AuditLog";
import AdminSettingsPage from "@/pages/admin/Settings";
import AdminNotificationsPage from "@/pages/admin/Notifications";
import AdminActivityFeedPage from "@/pages/admin/ActivityFeed";

// Protected Route Components
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const AdminRoute = ({ children }) => {
  const { isAdminAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!isAdminAuthenticated) {
    return <Navigate to="/admin-login" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/verify-otp" element={<VerifyOTPPage />} />
      <Route path="/admin-login" element={<AdminLoginPage />} />

      {/* User Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route index element={<UserDashboard />} />
        <Route path="deposits" element={<DepositsPage />} />
        <Route path="withdrawals" element={<WithdrawalsPage />} />
        <Route path="investments" element={<InvestmentsPage />} />
        <Route path="referrals" element={<ReferralsPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="profile" element={<ProfilePage />} />
      </Route>

      {/* Admin Protected Routes */}
      <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="users" element={<AdminUsersPage />} />
        <Route path="users/:userId" element={<AdminUserDetailPage />} />
        <Route path="deposits" element={<AdminDepositsPage />} />
        <Route path="withdrawals" element={<AdminWithdrawalsPage />} />
        <Route path="profits" element={<AdminProfitsPage />} />
        <Route path="activity-feed" element={<AdminActivityFeedPage />} />
        <Route path="audit-log" element={<AdminAuditLogPage />} />
        <Route path="settings" element={<AdminSettingsPage />} />
        <Route path="notifications" element={<AdminNotificationsPage />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster 
          position="top-right" 
          richColors 
          toastOptions={{
            style: {
              background: 'rgba(15, 22, 41, 0.95)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              color: '#e2e8f0',
            },
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
